<template>
  <label
    class="flex flex-row items-center whitespace-nowrap"
    :class="{
      'opacity-50': disabled,
      'cursor-pointer': !disabled,
    }"
  >
    <div class="relative" :class="{ 'hover-container': !disabled }">
      <div
        class="hover-circle absolute inset-0 -z-10 rounded-full bg-secondary-gray bg-opacity-10 opacity-0 transition-all duration-300 ease-in-out"
        :style="{
          width: hoverCircleSizes[size],
          height: hoverCircleSizes[size],
          top: `calc(50% - ${parseInt(hoverCircleSizes[size]) / 2}px)`,
          left: `calc(50% - ${parseInt(hoverCircleSizes[size]) / 2}px)`,
        }"
      ></div>

      <CheckboxRoot
        v-model:checked="currentValue"
        :name="uniqueInputName"
        :style="{
          height: checkboxSizes[size],
          width: checkboxSizes[size],
        }"
        :disabled="disabled"
        class="checkbox flex appearance-none items-center justify-center rounded-[3px] border border-solid border-theme-disabled-b transition-all"
        :class="[borderClasses]"
        v-on="validationListeners"
      >
        <CheckboxIndicator
          class="flex h-full w-full items-center justify-center"
          :class="{ 'rounded-sm': size === 'medium', 'rounded-md': size !== 'medium' }"
          :style="{
            width: checkboxSizes[size],
            height: checkboxSizes[size],
          }"
        >
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.61047 9L0 5.40347L1.64535 3.76448L3.61047 5.7278L9.35465 0L11 1.639L3.61047 9Z"
              fill="#FFFFFF"
            />
          </svg>
        </CheckboxIndicator>
      </CheckboxRoot>
    </div>

    <span class="select-none text-sm" :class="{ 'ml-2': type === 'secondary', 'ml-3': type === 'primary' }"
      ><slot></slot
    ></span>
  </label>

  <div
    name="error-message"
    class="overlay-collapsed"
    :class="{ 'overlay-expanded': !!errorMessage }"
    :style="animationStyle"
  >
    <small class="text-theme-error">{{ errorMessage || "&nbsp;" }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";
import { CheckboxIndicator, CheckboxRoot } from "radix-vue";
import { useField } from "vee-validate";
import { validateRules } from "@/utils/rules/rules";

type CheckboxType = "primary" | "secondary";
type CheckboxSize = "small" | "medium" | "large";

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue: boolean;
    disabled?: boolean;
    rules?: RuleFunction<boolean>[];
    type?: CheckboxType;
    size?: CheckboxSize;
  }>(),
  {
    disabled: false,
    rules: () => [],
    type: "primary",
    size: "large",
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const checkboxSizes: Record<CheckboxSize, string> = {
  small: "8px",
  medium: "16px",
  large: "20px",
};

const hoverCircleSizes: Record<CheckboxSize, string> = {
  small: "22px",
  medium: "30px",
  large: "38px",
};

const instance = getCurrentInstance();
let uniqueInputName = ref("input");
if (instance) {
  uniqueInputName.value = instance.uid.toString();
}

const {
  value: currentValue,
  errorMessage,
  handleChange,
} = useField(uniqueInputName.value, validateField, {
  syncVModel: true,
  initialValue: props.modelValue,
});

const validationListeners = {
  "update:checked": (e: boolean) => {
    handleChange(e);
    emit("update:modelValue", e);
  },
};

async function validateField(value: boolean) {
  return await validateRules(props.rules, value);
}

const animationDuration = 300;
const animationStyle = ref(`
  transition: opacity ${animationDuration}ms, visibility ${animationDuration}ms, max-height ${animationDuration}ms;
`);

const borderClasses = computed(() => ({
  "border-theme-error": errorMessage.value,
  "hover:border-theme-3/50": !props.disabled && props.type === "primary",
  "bg-white": !currentValue.value && props.type === "primary",
  "bg-theme-3 !border-theme-3": !props.disabled && currentValue.value && props.type === "primary",
  "hover:border-white hover:bg-dark-blue-50": !props.disabled && props.type === "secondary",
  "bg-dark-blue-50": currentValue.value && props.type === "secondary",
}));
</script>

<style lang="scss" scoped>
.overlay-collapsed {
  max-height: 0px;
  opacity: 0;
  visibility: hidden;
}

.overlay-expanded {
  max-height: 1000px;
  opacity: 1;
  visibility: visible;
  transition: max-height 0.5s ease;
  transition: visibility opacity 0.8s ease;
}

.hover-container {
  &:hover {
    .hover-circle {
      opacity: 1;
    }
  }
}
</style>
